import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Save, X, Upload, Compass,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';
const VIATOR_URL =
  'https://www.viator.com/partner-shop/mundalo?medium=link&medium_version=shop&campaign=Mundalotours';

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

const labelCls = 'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';
const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25';

/* ---------------------------- EDIT MODAL ---------------------------- */
const AdventureEditModal = ({ open, initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', blurb: '', image_url: '', viator_url: VIATOR_URL, active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const widgetRef = useRef(null);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || '',
      blurb: initial?.blurb || '',
      image_url: initial?.image_url || '',
      viator_url: initial?.viator_url || VIATOR_URL,
      active: initial?.active !== false,
    });
    loadCloudinaryScript();
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openUpload = () => {
    if (!window.cloudinary) return toast.error('Photo upload not ready.');
    setUploading(true);
    widgetRef.current = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'],
        multiple: false,
        resourceType: 'image',
        folder: 'mundalo_adventures',
        maxFileSize: 8_000_000,
        cropping: false,
      },
      (err, result) => {
        if (err) { setUploading(false); return toast.error('Upload failed.'); }
        if (result?.event === 'success') set('image_url', result.info.secure_url);
        if (result?.event === 'close' || result?.event === 'queues-end') setUploading(false);
      },
    );
    widgetRef.current.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      blurb: form.blurb.trim(),
      image_url: form.image_url.trim(),
      viator_url: (form.viator_url || VIATOR_URL).trim(),
      active: !!form.active,
    };
    if (!payload.name || !payload.blurb || !payload.image_url) {
      return toast.error('Name, blurb, and image are required.');
    }
    setSubmitting(true);
    try {
      const url = isEdit
        ? `${API}/api/admin/adventures/${initial.id}`
        : `${API}/api/admin/adventures`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(isEdit ? 'Adventure updated.' : 'Adventure created.');
      onSaved?.(data.adventure);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/45 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="adventure-edit-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border border-[#EFE6CF] rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#EFE6CF] sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-display italic text-2xl text-[#0A1F2E]">
            {isEdit ? 'Edit adventure' : 'New adventure'}
          </h2>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F4ECD8] flex items-center justify-center" data-testid="adventure-edit-close">
            <X className="w-4 h-4 text-[#0A1F2E]" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Name</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value.slice(0, 80))} maxLength={80} className={inputCls} data-testid="adventure-edit-name" />
          </div>
          <div>
            <label className={labelCls}>Blurb (one line)</label>
            <input type="text" value={form.blurb} onChange={(e) => set('blurb', e.target.value.slice(0, 200))} maxLength={200} className={inputCls} data-testid="adventure-edit-blurb" />
          </div>
          <div>
            <label className={labelCls}>Image</label>
            <div className="flex items-center gap-3">
              {form.image_url && (
                <img src={form.image_url} alt="" className="w-20 h-14 rounded-lg object-cover ring-2 ring-[#EFE6CF]" />
              )}
              <button type="button" onClick={openUpload} disabled={uploading} className="inline-flex items-center gap-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full" data-testid="adventure-edit-upload">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {form.image_url ? 'Replace' : 'Upload'}
              </button>
            </div>
            <input type="text" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="or paste image URL" className={`${inputCls} mt-2`} data-testid="adventure-edit-image-url" />
          </div>
          <div>
            <label className={labelCls}>Viator URL</label>
            <input type="text" value={form.viator_url} onChange={(e) => set('viator_url', e.target.value)} className={inputCls} data-testid="adventure-edit-viator-url" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="w-4 h-4 accent-[#2CA8A0]" data-testid="adventure-edit-active" />
            <span className="text-sm text-[#0A1F2E]">Active (visible on the Adventures page)</span>
          </label>
        </div>
        <footer className="px-5 py-4 border-t border-[#EFE6CF] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] px-4 py-2 rounded-full">Cancel</button>
          <button type="submit" disabled={submitting} className="font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white px-5 py-2 rounded-full inline-flex items-center gap-2" data-testid="adventure-edit-save">
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}<Save className="w-3.5 h-3.5" /> Save
          </button>
        </footer>
      </form>
    </div>
  );
};

/* ---------------------------- MAIN PAGE ---------------------------- */
const AdminAdventures = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/adventures`, {
        credentials: 'include', headers: authHeaders(),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not load adventures');
      setItems(d.adventures || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const onToggleActive = async (a) => {
    try {
      const res = await fetch(`${API}/api/admin/adventures/${a.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ active: !a.active }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      const d = await res.json();
      setItems((cur) => cur.map((x) => (x.id === a.id ? d.adventure : x)));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/adventures/${a.id}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      setItems((cur) => cur.filter((x) => x.id !== a.id));
      toast.success('Deleted.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Up/down arrow reorder. On every move we reflow `items` locally and persist
  // a bulk PUT /reorder so a refresh keeps the new order.
  const persistOrder = async (next) => {
    setSavingOrder(true);
    try {
      const payload = {
        items: next.map((x, i) => ({ id: x.id, sort_order: i })),
      };
      const res = await fetch(`${API}/api/admin/adventures/reorder`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Reorder failed');
    } catch (e) {
      toast.error(e.message);
      fetchList(); // revert from server on failure
    } finally {
      setSavingOrder(false);
    }
  };

  const move = (id, dir) => {
    setItems((cur) => {
      const idx = cur.findIndex((x) => x.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= cur.length) return cur;
      const next = cur.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      persistOrder(next);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="admin-adventures-page">
        <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">Admin · Adventures</p>
            <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-2">Adventures</h1>
            <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
              Edit the activity grid that powers <span className="font-mono">/adventures</span> and <span className="font-mono">/portal/adventures</span>. Use the up/down arrows to reorder.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full self-start md:self-auto"
            data-testid="admin-adventures-new-btn"
          >
            <Plus className="w-4 h-4" /> New adventure
          </button>
        </header>

        {savingOrder && (
          <p className="text-xs text-[#1A5C6E] mb-3 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving order…</p>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" /></div>
        )}
        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 rounded-xl text-sm" data-testid="admin-adventures-error">{error}</div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center" data-testid="admin-adventures-empty">
            <Compass className="w-10 h-10 text-[#1A5C6E]/40 mx-auto mb-3" />
            <p className="text-[#0A1F2E]/65">No adventures yet - click “New adventure”.</p>
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <ul className="space-y-2" data-testid="admin-adventures-list">
            {items.map((a, idx) => (
              <li
                key={a.id}
                className="bg-white border border-[#EFE6CF] rounded-2xl p-3 sm:p-4 flex items-center gap-4 transition"
                data-testid={`admin-adventure-row-${a.id}`}
              >
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(a.id, -1)}
                    disabled={idx === 0 || savingOrder}
                    className="w-7 h-7 rounded-full border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#F4ECD8] flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                    aria-label={`Move ${a.name} up`}
                    data-testid={`admin-adventure-up-${a.id}`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(a.id, 1)}
                    disabled={idx === items.length - 1 || savingOrder}
                    className="w-7 h-7 rounded-full border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#F4ECD8] flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                    aria-label={`Move ${a.name} down`}
                    data-testid={`admin-adventure-down-${a.id}`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                {a.image_url ? (
                  <img src={a.image_url} alt="" className="w-16 h-12 rounded-md object-cover ring-1 ring-[#EFE6CF] shrink-0" />
                ) : (
                  <div className="w-16 h-12 rounded-md bg-[#F4ECD8] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display italic text-lg text-[#0A1F2E] truncate">{a.name}</p>
                  <p className="text-xs text-[#0A1F2E]/60 truncate">{a.blurb}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onToggleActive(a)}
                  className={`inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-1.5 rounded-full transition shrink-0 ${
                    a.active
                      ? 'bg-[#2CA8A0]/15 text-[#1A5C6E] hover:bg-[#2CA8A0]/25'
                      : 'bg-[#EFE6CF] text-[#0A1F2E]/55 hover:bg-[#F4ECD8]'
                  }`}
                  title={a.active ? 'Visible' : 'Hidden'}
                  data-testid={`admin-adventure-toggle-${a.id}`}
                >
                  {a.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {a.active ? 'Active' : 'Hidden'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(a); setModalOpen(true); }}
                  className="font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] px-3 py-1.5 rounded-full"
                  data-testid={`admin-adventure-edit-${a.id}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(a)}
                  className="inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#B91C1C]/35 hover:bg-[#B91C1C]/10 text-[#B91C1C] px-3 py-1.5 rounded-full"
                  data-testid={`admin-adventure-delete-${a.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </li>
            ))}
          </ul>
        )}

        <AdventureEditModal
          open={modalOpen}
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={(a) => {
            setItems((cur) => {
              const idx = cur.findIndex((x) => x.id === a.id);
              if (idx >= 0) {
                const next = cur.slice();
                next[idx] = a;
                return next;
              }
              return [...cur, a];
            });
          }}
        />
      </div>
    </div>
  );
};

export default AdminAdventures;
